import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileEdit, X } from "lucide-react";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import { Button } from "@stockline/ui/components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { DynamicCustomFields } from "@/components/pre-orders/DynamicPreOrderFlowFields";
import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";
import { filterDefsByMode } from "../../config/customColumns";
import { useRecapModeOptional } from "../../contexts/RecapContext";
import { isPreOrderModifiable } from "../../services/recapService";
import {
  AiCustomFieldBadge,
  formatCustomFieldValue,
  isAiPendingCustomField,
} from "./AiCustomFieldBadge";
import type { FlowCellProps } from "./types";

type CustomFieldValues = Record<string, string | number | boolean>;

interface CustomFieldsCellProps extends FlowCellProps {
  disabled?: boolean;
}

/**
 * Renders a single button per row with a `${filled}/${total}` badge.
 * Clicking opens an anchored popover with the full `DynamicCustomFields`
 * form. Values persist immediately to `flow.flow_metadata` (JSONB) via
 * `onFlowUpdate` — no confirm button; closing by clicking outside or the
 * X is enough.
 *
 * Visibility is gated upstream in `useRecapColumns` by the
 * `preOrderFlowCustomFields` feature flag — when it has no entries the
 * column is dropped from the registry so the cell never mounts.
 */
export function CustomFieldsCell({
  flow,
  preOrder,
  disabled,
  onFlowUpdate,
}: Readonly<CustomFieldsCellProps>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Per-def mode scoping mirrors `useRecapColumns` — outside a RecapProvider
  // (mode null) all defs apply.
  const mode = useRecapModeOptional();
  const fields = filterDefsByMode(
    (useFeatureFlag("preOrderFlowCustomFields") as
      | CustomFieldDefinition[]
      | undefined) ?? [],
    mode,
  );

  const isLocked = disabled || !isPreOrderModifiable(preOrder);

  const values: CustomFieldValues =
    (flow.flow_metadata as CustomFieldValues | null | undefined) ?? {};

  // Legacy parity: a key counts as "filled" only when its value is truthy.
  // 0 and false are treated as unfilled — matches the legacy badge UX.
  const filledCount = fields.filter((f) => !!values[f.key]).length;

  const handleChange = (next: CustomFieldValues) => {
    onFlowUpdate(flow.id, {
      flow_metadata: {
        ...flow.flow_metadata,
        ...next,
      },
    });
  };

  if (fields.length === 0) return null;

  // AI proposals on custom fields are staged inside the flat `flow_metadata`
  // lane. The popover collapses every field into one button, so surface each
  // pending "from → new" diff as a stacked badge below it (mirrors the inline
  // field cell used on wide screens).
  const pendingFields = fields.filter((f) =>
    isAiPendingCustomField(flow, f.key),
  );

  const makeFormat =
    (def: CustomFieldDefinition) =>
    (v: unknown): string =>
      formatCustomFieldValue(def, v, t);

  const popover = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isLocked}
          className="h-7 w-full"
          data-testid="custom-fields-cell-trigger"
          // Mode-independent guide handle: the same attr rides the inline
          // custom-field cells (≥1280px, where this popover is dropped for
          // per-field columns), so the guidance overlay finds the custom
          // fields in either responsive rendering.
          data-attr="flow-custom-fields"
        >
          <FileEdit className="h-3 w-3 mr-1" />
          {filledCount > 0 ? `${filledCount}/${fields.length}` : "—"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-[min(520px,95vw)] p-0 border-2 border-blue-400 shadow-lg bg-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
          <span className="text-sm font-medium text-gray-700">
            {t("ordersRecap.customFields", "Champs personnalisés")}
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t("common.close", "Fermer")}
            data-testid="custom-fields-cell-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="p-3">
          <DynamicCustomFields
            fields={fields}
            values={values}
            onChange={handleChange}
            disabled={isLocked}
            compact={false}
          />
        </div>
      </PopoverContent>
    </Popover>
  );

  // Guide stand-ins: per-field markers co-located with the trigger, so the
  // guidance overlay can spotlight ONE custom field even in this collapsed
  // rendering where every field shares a single popover. `absolute inset-0`
  // gives each a real (trigger-sized) rect — the overlay measures with
  // getBoundingClientRect, which a `hidden` element would report as zero. In
  // the inline rendering these ids live on the per-field cells instead.
  const cellWithMarkers = (
    <div className="relative">
      {popover}
      {fields.map((f) => (
        <span
          key={f.key}
          aria-hidden="true"
          data-testid={`flow-custom-field-${f.key}`}
          className="pointer-events-none absolute inset-0"
        />
      ))}
    </div>
  );

  if (pendingFields.length === 0) return cellWithMarkers;

  return (
    <div className="flex min-w-0 flex-col">
      {cellWithMarkers}
      <div className="mt-0.5 flex flex-col gap-0.5">
        {pendingFields.map((f) => (
          <div key={f.key} className="flex items-center gap-1">
            <span className="text-[10px] font-medium text-muted-foreground shrink-0">
              {f.label}
            </span>
            <AiCustomFieldBadge
              flow={flow}
              fieldKey={f.key}
              format={makeFormat(f)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
